import { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { validateUpload } from "../lib/uploadLimits";
import MentionTextarea from "./MentionTextarea";
import AuthImage from "./AuthImage";
import { useAuth } from "../contexts/AuthContext";
import { ImagePlus, Video, Link2, BarChart3, X, Loader2 } from "lucide-react";

const POLL_DURATIONS = [
  { value: "1d", label: "1 day" },
  { value: "3d", label: "3 days" },
  { value: "7d", label: "7 days" },
  { value: "14d", label: "14 days" },
  { value: "never", label: "No expiry" },
];

export default function PostComposer({ onPosted }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [media, setMedia] = useState([]); // {file_id, kind, url, content_type}
  const [linkUrl, setLinkUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);
  const [showPoll, setShowPoll] = useState(false);
  const [poll, setPoll] = useState({
    question: "",
    options: ["", ""],
    duration: "3d",
    visibility: "public",
  });
  const [postAsOrg, setPostAsOrg] = useState(false);
  const [uploading, setUploading] = useState(null); // null | "image" | "video"
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleUpload = async (file, kind) => {
    if (!file) return;
    setError("");
    const backendKind = kind === "video" ? "post_video" : "post_photo";
    const sizeErr = validateUpload(backendKind, file);
    if (sizeErr) { setError(sizeErr); return; }
    setUploading(kind === "video" ? "video" : "image");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads/${backendKind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMedia((m) => [...m, { file_id: data.file_id, kind, url: data.url, content_type: data.content_type }]);
    } catch (e) { setError(formatApiError(e)); }
    finally { setUploading(null); }
  };

  const removeMedia = (id) => setMedia((m) => m.filter((x) => x.file_id !== id));

  const previewLink = async () => {
    if (!linkUrl.trim()) return;
    try {
      const { data } = await api.get(`/posts/preview-link`, { params: { url: linkUrl.trim() } });
      if (data.kind === "video") {
        setVideoUrl(data.video_embed.url);
        setLinkPreview(null);
      } else {
        setLinkPreview(data.link_preview);
      }
    } catch (err) {
      // Link preview failures are non-blocking — user can still post a raw URL.
      console.warn("[link-preview] failed:", err?.response?.status || err?.message);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (posting) return;
    setError("");
    setPosting(true);
    try {
      const body = {
        content,
        media: media.map((m) => ({ file_id: m.file_id, kind: m.kind })),
        link_url: linkUrl.trim() || null,
        video_url: videoUrl.trim() || null,
        post_as_org: postAsOrg && !!user?.managed_org_id,
        poll: showPoll
          ? {
              question: poll.question,
              options: poll.options.map((o) => o.trim()).filter(Boolean),
              duration: poll.duration,
              visibility: poll.visibility,
            }
          : null,
      };
      const { data } = await api.post(`/posts`, body);
      onPosted?.(data);
      setContent("");
      setMedia([]);
      setLinkUrl("");
      setVideoUrl("");
      setLinkPreview(null);
      setShowPoll(false);
      setPoll({ question: "", options: ["", ""], duration: "3d", visibility: "public" });
    } catch (e) { setError(formatApiError(e)); }
    finally { setPosting(false); }
  };

  const addOpt = () => setPoll((p) => p.options.length < 6 ? { ...p, options: [...p.options, ""] } : p);
  const removeOpt = (i) => setPoll((p) => ({ ...p, options: p.options.filter((_, idx) => idx !== i) }));
  const setOpt = (i, v) => setPoll((p) => ({ ...p, options: p.options.map((o, idx) => idx === i ? v : o) }));

  return (
    <form onSubmit={submit} className="csc-card csc-overflow-safe p-5 space-y-3" data-testid="post-composer">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full overflow-hidden border border-[rgba(0,170,255,0.18)] shrink-0">
          <AuthImage
            src={user?.avatar_url}
            alt=""
            className="w-full h-full object-cover"
            fallback={<div className="w-full h-full bg-[#0d1b2e] flex items-center justify-center font-mono-tech text-[#94a3b8] text-xs">{user?.first_name?.[0] || "?"}</div>}
          />
        </div>
        <div className="flex-1 min-w-0">
          <MentionTextarea
            value={content}
            onChange={setContent}
            placeholder="What's on your mind? Use @ to mention, # for hashtags…"
            rows={3}
            data-testid="composer-textarea"
          />
        </div>
      </div>

      {media.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="composer-media">
          {media.map((m) => (
            <div key={m.file_id} className="relative w-24 h-24 rounded-md overflow-hidden border border-[rgba(0,170,255,0.18)]">
              {m.kind === "video" ? (
                <div className="w-full h-full bg-black text-white text-[10px] font-mono-tech flex items-center justify-center">VIDEO</div>
              ) : (
                <AuthImage src={m.url} alt="" className="w-full h-full object-cover" />
              )}
              <button type="button" onClick={() => removeMedia(m.file_id)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-red-500/80">
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {(linkUrl || videoUrl) && (
        <div className="flex gap-2 flex-wrap min-w-0">
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onBlur={previewLink}
            placeholder="https://… (link or YouTube/Vimeo URL)"
            className="csc-input !py-2 flex-1 min-w-0"
            data-testid="composer-link-input"
          />
          <button type="button" onClick={previewLink} className="csc-btn-ghost !py-2 !px-3">Preview</button>
        </div>
      )}

      {linkPreview && (
        <div className="text-xs text-[#94a3b8] border border-[rgba(0,170,255,0.18)] rounded-md p-3 flex items-center gap-3 min-w-0">
          {linkPreview.image && <img src={linkPreview.image} alt="" className="w-12 h-12 object-cover rounded shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="font-mono-tech text-[10px] uppercase tracking-wider">{linkPreview.site_name}</div>
            <div className="text-white truncate">{linkPreview.title}</div>
          </div>
        </div>
      )}

      {showPoll && (
        <div className="rounded-md border border-[rgba(0,170,255,0.18)] p-4 space-y-3" data-testid="poll-builder">
          <div className="flex items-center justify-between">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#39ff14]">Poll</div>
            <button type="button" onClick={() => setShowPoll(false)} className="text-xs text-[#94a3b8] hover:text-red-400">Remove poll</button>
          </div>
          <input
            value={poll.question}
            onChange={(e) => setPoll({ ...poll, question: e.target.value })}
            placeholder="Ask a question…"
            className="csc-input"
            data-testid="poll-question-input"
          />
          {poll.options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={opt}
                onChange={(e) => setOpt(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                className="csc-input flex-1"
                data-testid={`poll-option-input-${i}`}
              />
              {poll.options.length > 2 && (
                <button type="button" onClick={() => removeOpt(i)} className="csc-btn-ghost !py-2 !px-3"><X size={12} /></button>
              )}
            </div>
          ))}
          {poll.options.length < 6 && (
            <button type="button" onClick={addOpt} className="text-xs font-mono-tech uppercase tracking-wider text-[#00aaff] hover:text-[#39ff14]" data-testid="add-option-btn">+ Add option</button>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <select value={poll.duration} onChange={(e) => setPoll({ ...poll, duration: e.target.value })} className="csc-input !py-2" data-testid="poll-duration">
              {POLL_DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <select value={poll.visibility} onChange={(e) => setPoll({ ...poll, visibility: e.target.value })} className="csc-input !py-2" data-testid="poll-visibility">
              <option value="public">Public results</option>
              <option value="anonymous">Anonymous</option>
            </select>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-[rgba(0,170,255,0.18)]">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => photoInputRef.current?.click()} className="csc-btn-ghost !py-1.5 !px-2.5" data-testid="add-photo-btn" disabled={!!uploading}>
            {uploading === "image" ? <Loader2 className="animate-spin" size={13} /> : <ImagePlus size={13} />} Photo
          </button>
          <button type="button" onClick={() => videoInputRef.current?.click()} className="csc-btn-ghost !py-1.5 !px-2.5" data-testid="add-video-btn" disabled={!!uploading}>
            {uploading === "video" ? <Loader2 className="animate-spin" size={13} /> : <Video size={13} />} Video
          </button>
          <button type="button" onClick={() => setLinkUrl((u) => u || "https://")} className="csc-btn-ghost !py-1.5 !px-2.5" data-testid="add-link-btn">
            <Link2 size={13} /> Link
          </button>
          <button type="button" onClick={() => setShowPoll(true)} className="csc-btn-ghost !py-1.5 !px-2.5" data-testid="add-poll-btn">
            <BarChart3 size={13} /> Poll
          </button>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0], "photo")} />
          <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0], "video")} />
        </div>
        <div className="flex items-center gap-3">
          {user?.managed_org_id && (
            <label className="flex items-center gap-2 text-[11px] font-mono-tech uppercase tracking-wider text-[#94a3b8] cursor-pointer">
              <input type="checkbox" checked={postAsOrg} onChange={(e) => setPostAsOrg(e.target.checked)} className="accent-[#00aaff]" data-testid="post-as-org-toggle" />
              Post as organization
            </label>
          )}
          <button type="submit" disabled={posting} className="csc-btn-primary" data-testid="composer-submit">
            {posting ? "Posting…" : "Post"}
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-400" data-testid="composer-error">{error}</div>}
    </form>
  );
}
